import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Capacitor } from '@capacitor/core';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { KlFullModal } from './modal/KlFullModal';
import { KlMenu } from './modal/KlMenu';
import { KlDialog } from './modal/KlDialog';
import { supabase } from '../../supabaseClient';
import {
    Task, PhysicalUnit, Level, ProtocolConfig, ProtocolState,
    ProtocolHistoryEntry, Responsible, GlobalUser, Activity
} from '../../types';
import { optimizeSignatureCanvas, uploadSignatureIfOnline } from '../../utils/signatureUtils';
import { cleanTaskName, findSavedProtocolEntry, computeCanonicalProtocolId, normalizeProtocolChecklist, isProtocolTemplateChangedWithCompletedParams } from '../../utils/planningUtils';
import {
    isPendingPhotoMarker, enqueuePendingPhoto, removePendingPhoto,
    setupPendingPhotosSync, dataUrlToBlob, uploadPhotoToStorage
} from '../../utils/pendingPhotosQueue';
import { BufferedTextArea } from '../ui/BufferedTextArea';
import { approveProtocol, rejectProtocol, adminLiberateProtocol, hasReviewerAddedPhoto } from '../../domain/protocolWorkflow';

export interface MobileProtocolModalProps {
    open: boolean;
    onClose: () => void;
    taskId: string | null;
    unitId: string | null;
    taskName?: string;
    taskCode?: string;
    tasks: Task[];
    units: PhysicalUnit[];
    levels: Level[];
    protocolConfigs: Record<string, ProtocolConfig>;
    savedProtocols: Record<string, ProtocolState>;
    setSavedProtocols: React.Dispatch<React.SetStateAction<Record<string, ProtocolState>>>;
    currentUser: GlobalUser | null;
    hasPermission: (id: string, action: 'view' | 'edit') => boolean;
    responsibles?: Responsible[];
    addActivity?: (activity: Activity) => void;
    collectionName?: string;
    matrixData?: Record<string, any>;
}

// Helpers para URLs y subida de imágenes
const getPhotoUrl = (photoPath?: string): string => {
    if (!photoPath || isPendingPhotoMarker(photoPath)) return '';
    if (photoPath.startsWith('data:image') || photoPath.startsWith('http://') || photoPath.startsWith('https://') || photoPath.startsWith('blob:')) {
        return photoPath;
    }
    if (photoPath.length > 500 && !photoPath.includes('/')) {
        return `data:image/jpeg;base64,${photoPath}`;
    }
    const cleanPath = photoPath.replace(/^\/+/, '');
    const bucketName = (cleanPath.startsWith('quality_photos/') || cleanPath.startsWith('quality_signatures/'))
        ? 'graphic_plans'
        : 'quality_photos';
    const { data } = supabase.storage.from(bucketName).getPublicUrl(cleanPath);
    return data.publicUrl;
};

const compressAndConvertPhoto = (file: File): Promise<string> => {
    return new Promise((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (event) => {
            const img = new Image();
            img.src = event.target?.result as string;
            img.onload = () => {
                const canvas = document.createElement('canvas');
                const maxDim = 800;
                let width = img.width;
                let height = img.height;
                if (width > height) {
                    if (width > maxDim) {
                        height = Math.round((height * maxDim) / width);
                        width = maxDim;
                    }
                } else {
                    if (height > maxDim) {
                        width = Math.round((width * maxDim) / height);
                        height = maxDim;
                    }
                }
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                if (ctx) {
                    ctx.drawImage(img, 0, 0, width, height);
                    resolve(canvas.toDataURL('image/jpeg', 0.6));
                } else {
                    resolve(event.target?.result as string);
                }
            };
            img.onerror = () => resolve(event.target?.result as string);
        };
        reader.onerror = () => resolve('');
    });
};

export const MobileProtocolModal: React.FC<MobileProtocolModalProps> = ({
    open,
    onClose,
    taskId,
    unitId,
    taskName: propTaskName,
    taskCode: propTaskCode,
    tasks,
    units,
    levels,
    protocolConfigs,
    savedProtocols,
    setSavedProtocols,
    currentUser,
    hasPermission,
    responsibles = [],
    addActivity,
    collectionName,
    matrixData
}) => {
    // Si no hay target válido, no renderizar
    if (!taskId || !unitId) return null;

    const unit = useMemo(() => units.find(u => u.id === unitId), [units, unitId]);
    const level = useMemo(() => levels.find(l => l.id === unit?.levelId), [levels, unit]);
    const config = useMemo(() => protocolConfigs[taskId] || null, [protocolConfigs, taskId]);

    // Buscar si ya existe guardado (eligiendo la entrada más reciente/avanzada si hay más de una)
    const resolvedEntry = useMemo(() => {
        const canonicalCandidate = (propTaskCode && propTaskCode.includes('-'))
            ? propTaskCode
            : (matrixData ? computeCanonicalProtocolId(taskId, unitId, tasks, units, protocolConfigs, matrixData) : null);
        return findSavedProtocolEntry(savedProtocols, canonicalCandidate, taskId, unitId);
    }, [savedProtocols, propTaskCode, matrixData, taskId, unitId, tasks, units, protocolConfigs]);

    const protocolId = useMemo(() => {
        if (propTaskCode && propTaskCode.includes('-')) return propTaskCode;
        if (matrixData) {
            const canonical = computeCanonicalProtocolId(taskId, unitId, tasks, units, protocolConfigs, matrixData);
            if (canonical) return canonical;
        }
        if (resolvedEntry) return resolvedEntry.key;
        const codePrefix = config?.code || propTaskCode || 'PRT';
        return `${codePrefix}-${unit?.name || '001'}`;
    }, [propTaskCode, matrixData, taskId, unitId, tasks, units, protocolConfigs, resolvedEntry, config, unit]);

    const syncMobileProtocolKeys = (
        prev: Record<string, ProtocolState>,
        targetId: string,
        newState: ProtocolState
    ): Record<string, ProtocolState> => {
        const entry = findSavedProtocolEntry(prev, targetId, taskId, unitId);
        const updated: Record<string, ProtocolState> = {
            ...prev,
            [targetId]: { ...newState, id: targetId }
        };
        if (entry) {
            entry.allKeys.forEach(k => {
                if (k !== targetId) {
                    updated[k] = { ...newState, id: targetId };
                }
            });
        }
        return updated;
    };

    const taskName = propTaskName || config?.protocolName || 'Partida';
    const unitName = unit?.name || 'Unidad';
    const levelName = level?.name || '-';

    const group = useMemo(() => {
        return config?.reviewerGroups?.find(g => g.unitIds.includes(unitId));
    }, [config, unitId]);

    // Estado del protocolo
    const currentState: ProtocolState = useMemo(() => {
        if (resolvedEntry) {
            const isTemplateChanged = isProtocolTemplateChangedWithCompletedParams(resolvedEntry.state, config);
            return {
                ...resolvedEntry.state,
                id: protocolId,
                taskId: resolvedEntry.state.taskId || taskId,
                unitId: resolvedEntry.state.unitId || unitId,
                status: isTemplateChanged ? 'Abierto' : resolvedEntry.state.status,
                currentReviewerIndex: isTemplateChanged ? 0 : (resolvedEntry.state.currentReviewerIndex || 0),
                checklist: isTemplateChanged ? {} : (resolvedEntry.state.checklist || {}),
                signatures: isTemplateChanged ? {} : (resolvedEntry.state.signatures || {}),
                protocolName: config?.protocolName || resolvedEntry.state.protocolName
            };
        }
        return {
            id: protocolId,
            taskId,
            unitId,
            groupId: group?.id || '',
            status: 'Abierto',
            currentReviewerIndex: 0,
            checklist: {},
            signatures: {},
            history: [],
            creationDate: new Date().toLocaleDateString('es-CL'),
            taskName,
            protocolName: config?.protocolName || 'Protocolo'
        };
    }, [resolvedEntry, protocolId, taskId, unitId, group, taskName, config]);

    // Checklist temporal local
    const [tempChecklist, setTempChecklist] = useState<Record<string, { status: 'pass' | 'fail' | 'na' | 'pending'; comment: string; photos: string[] }>>({});
    const [currentSignature, setCurrentSignature] = useState<string | null>(null);
    const [ratifiedItemIds, setRatifiedItemIds] = useState<Set<string>>(new Set());

    // Rechazo y Resolución
    const [resolutionText, setResolutionText] = useState("");
    const [resolutionPhotos, setResolutionPhotos] = useState<string[]>([]);
    const [rejectionModalOpen, setRejectionModalOpen] = useState(false);
    const [rejectionReason, setRejectionReason] = useState("");

    // Modales secundarios de ítem
    const [activeCommentItem, setActiveCommentItem] = useState<{ id: string; text: string } | null>(null);
    const [activePhotoItem, setActivePhotoItem] = useState<string | null>(null);
    const [activeInfoItem, setActiveInfoItem] = useState<{ id: string; text: string } | null>(null);
    const [signaturePadOpen, setSignaturePadOpen] = useState(false);
    const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
    const [showValidationHighlights, setShowValidationHighlights] = useState(false);
    const [historyModalOpen, setHistoryModalOpen] = useState(false);
    const [headerMenuAnchor, setHeaderMenuAnchor] = useState<HTMLElement | null>(null);
    const [isHeaderMenuOpen, setIsHeaderMenuOpen] = useState(false);
    const [alertDialog, setAlertDialog] = useState<{ open: boolean; title: string; message: string; danger?: boolean }>({
        open: false,
        title: '',
        message: ''
    });
    const showMobileAlert = (title: string, message: string, danger = false) => {
        setAlertDialog({ open: true, title, message, danger });
    };

    const [cameraPermissionDialog, setCameraPermissionDialog] = useState(false);
    const [noCameraDialog, setNoCameraDialog] = useState(false);
    const handleOpenSettings = async () => {
        setCameraPermissionDialog(false);
        try {
            if (Capacitor.isNativePlatform()) {
                if (Capacitor.getPlatform() === 'ios') {
                    window.location.href = 'app-settings:';
                } else {
                    await Camera.requestPermissions({ permissions: ['camera'] });
                }
            }
        } catch (e) {
            console.warn("[MobileProtocolModal] Error opening settings:", e);
        }
    };

    // Auditoría de montado y desmontado
    useEffect(() => {
        console.log('[MobileProtocolModal] Mount', new Date().toISOString());
        return () => console.log('[MobileProtocolModal] Unmount', new Date().toISOString());
    }, []);

    // Sincronización automática de fotos pendientes en IndexedDB al recuperar señal o abrir
    useEffect(() => {
        if (!open) return;
        const cleanup = setupPendingPhotosSync(({ protocolId: pId, itemId: itId, marker, storageUrl }) => {
            setTempChecklist(prev => {
                const curItem = prev[itId];
                if (!curItem || !curItem.photos) return prev;
                return {
                    ...prev,
                    [itId]: {
                        ...curItem,
                        photos: curItem.photos.map(p => p === marker ? storageUrl : p)
                    }
                };
            });

            setSavedProtocols(prev => {
                let changed = false;
                const next = { ...prev };
                for (const [k, proto] of Object.entries(next)) {
                    if (!proto || !proto.checklist) continue;
                    const item = proto.checklist[itId];
                    if (item && Array.isArray(item.photos) && item.photos.includes(marker)) {
                        changed = true;
                        next[k] = {
                            ...proto,
                            checklist: {
                                ...proto.checklist,
                                [itId]: {
                                    ...item,
                                    photos: item.photos.map(p => p === marker ? storageUrl : p)
                                }
                            },
                            updatedAt: new Date().toISOString()
                        };
                    }
                }
                return changed ? next : prev;
            });
        });

        return cleanup;
    }, [open, setSavedProtocols]);

    // Ref para rastrear si el modal acaba de abrirse o si cambió el protocolo activo
    const lastOpenedProtocolKeyRef = useRef<string | null>(null);

    // Inicializar checklist al abrir
    useEffect(() => {
        if (!open) {
            lastOpenedProtocolKeyRef.current = null;
            return;
        }

        const currentKey = `${protocolId}_${currentState.currentReviewerIndex}`;
        const isNewOpenOrTarget = lastOpenedProtocolKeyRef.current !== currentKey;

        if (isNewOpenOrTarget) {
            lastOpenedProtocolKeyRef.current = currentKey;
            const isTemplateChanged = isProtocolTemplateChangedWithCompletedParams(currentState, config);
            if (isTemplateChanged) {
                setTempChecklist({});
                setCurrentSignature(null);
                setRatifiedItemIds(new Set());
                setShowValidationHighlights(false);
                showMobileAlert(
                    'Cambio de Plantilla Base',
                    'Se cambio la plantilla base del protocolo. Realizar protocolo de nuevo',
                    false
                );
            } else {
                const initial = normalizeProtocolChecklist(currentState.checklist, config?.items, currentState, config);
                setTempChecklist(initial);
                setCurrentSignature(currentState.signatures[currentState.currentReviewerIndex] || null);
                setRatifiedItemIds(new Set());
                setShowValidationHighlights(false);
            }
        } else {
            // Si el modal ya está abierto y currentState cambia (ej. por sincronización de fondo),
            // fusionar cuidadosamente SIN sobrescribir fotos ni comentarios locales en edición
            const isTemplateChanged = isProtocolTemplateChangedWithCompletedParams(currentState, config);
            if (isTemplateChanged) {
                setTempChecklist({});
                return;
            }
            setTempChecklist(prev => {
                const merged = { ...prev };
                const normalizedRemote = normalizeProtocolChecklist(currentState.checklist, config?.items, currentState, config);
                Object.keys(normalizedRemote).forEach(key => {
                    const remoteItem = normalizedRemote[key];
                    const localItem = merged[key];
                    if (!localItem) {
                        merged[key] = {
                            status: remoteItem.status || 'pending',
                            comment: remoteItem.comment || '',
                            photos: Array.isArray(remoteItem.photos) ? [...remoteItem.photos] : []
                        };
                    } else {
                        // Unir fotos remotas preservando las fotos añadidas localmente
                        const combinedPhotos = Array.from(new Set([
                            ...(localItem.photos || []),
                            ...(remoteItem.photos || [])
                        ]));
                        merged[key] = {
                            ...localItem,
                            photos: combinedPhotos
                        };
                    }
                });
                return merged;
            });
        }
    }, [open, protocolId, currentState, config?.items]);

    // Permisos
    const isProjectAdmin = (): boolean => {
        if (!currentUser) return false;
        if (currentUser.globalRole === 'Administrador' || (currentUser.globalRole as any) === 'SYSADMIN') return true;
        if ((currentUser as any).role?.toLowerCase().includes('admin')) return true;
        if ((currentUser as any).profileName?.toLowerCase().includes('admin')) return true;
        const hasAdminAssignment = currentUser.assignedProjects?.some(p =>
            p.profileName === 'Administrador' ||
            p.profileName === 'Administrador del Proyecto' ||
            p.profileName?.toLowerCase().includes('admin')
        );
        return !!hasAdminAssignment;
    };

    const hasEditPermission = hasPermission('movil_cc_completar', 'edit') || hasPermission('cc_completar', 'edit');

    const isUserTurn = useMemo((): boolean => {
        if (!currentUser) return false;
        if (isProjectAdmin()) return true;
        if (!group || !group.reviewers || group.reviewers.length === 0) return true;
        const currentReviewer = group.reviewers[currentState.currentReviewerIndex];
        if (!currentReviewer) return false;

        const userEmail = (currentUser.email || '').toLowerCase().trim();
        const userName = (currentUser.name || '').toLowerCase().trim();
        const revName = (currentReviewer.name || '').toLowerCase().trim();

        if (revName && userName && revName === userName) return true;

        const currentResp = responsibles.find(r => r.id === currentReviewer.id);
        if (currentResp) {
            const respEmail = (currentResp.email || '').toLowerCase().trim();
            const respName = (currentResp.name || '').toLowerCase().trim();
            if (respEmail && userEmail && respEmail === userEmail) return true;
            if (respName && userName && respName === userName) return true;
        }

        return false;
    }, [currentUser, group, currentState.currentReviewerIndex, responsibles]);

    const isEditable = hasEditPermission && isUserTurn && currentState.status !== 'Cerrado';
    const isRejected = currentState.status === 'Rechazado';
    const canReject = currentState.currentReviewerIndex > 0;
    const items = config?.items || [];
    const currentReviewerName = group?.reviewers[currentState.currentReviewerIndex]?.name || `Revisor ${(currentState.currentReviewerIndex || 0) + 1}`;

    const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);

    // Handlers
    const handleChecklistClick = (itemId: string, status: 'pass' | 'fail' | 'na') => {
        if (!isEditable) return;
        setTempChecklist(prev => ({
            ...prev,
            [itemId]: {
                ...(prev[itemId] || { comment: '', photos: [] }),
                status
            }
        }));
        setRatifiedItemIds(prev => new Set(prev).add(itemId));
    };

    const handleAddPhoto = async (itemId: string, e?: React.ChangeEvent<HTMLInputElement>) => {
        if (!isEditable) return;

        // 1. Si estamos en plataforma nativa de Capacitor, usar Camera plugin nativo
        if (Capacitor.isNativePlatform()) {
            (window as any).__klyd_camera_in_progress = true;
            try {
                // Chequear o solicitar permiso de cámara
                try {
                    const check = await Camera.checkPermissions();
                    if (check.camera === 'denied') {
                        setCameraPermissionDialog(true);
                        return;
                    }
                    if (check.camera === 'prompt' || check.camera === 'prompt-with-rationale') {
                        const req = await Camera.requestPermissions({ permissions: ['camera'] });
                        if (req.camera === 'denied') {
                            setCameraPermissionDialog(true);
                            return;
                        }
                    }
                } catch (permErr) {
                    console.warn("[handleAddPhoto] Camera permission check warning:", permErr);
                }

                setIsUploadingPhoto(true);
                const image = await Camera.getPhoto({
                    quality: 70,
                    width: 1600,
                    allowEditing: false,
                    resultType: CameraResultType.DataUrl,
                    source: CameraSource.Camera,
                    saveToGallery: false
                });

                if (image && image.dataUrl) {
                    let photoUrl = '';
                    let uploadFailed = false;
                    try {
                        const blob = dataUrlToBlob(image.dataUrl);
                        photoUrl = await uploadPhotoToStorage(blob, protocolId, itemId);
                    } catch (uploadErr) {
                        console.warn("[handleAddPhoto] Storage upload failed, guardando en cola local IndexedDB:", uploadErr);
                        uploadFailed = true;
                        // La foto pendiente no se guarda en savedProtocols ni en app_data.
                        // Queda solo en el teléfono en IndexedDB, asociada al protocolo y al parámetro.
                        const pendingItem = await enqueuePendingPhoto({
                            protocolId,
                            itemId,
                            dataUrl: image.dataUrl,
                            collectionName
                        });
                        photoUrl = pendingItem.marker;
                    }

                    if (photoUrl) {
                        setTempChecklist(prev => {
                            const curItem = prev[itemId] || { status: 'pending', comment: '', photos: [] };
                            const updatedPhotos = [...(curItem.photos || []), photoUrl];
                            return {
                                ...prev,
                                [itemId]: {
                                    ...curItem,
                                    photos: updatedPhotos
                                }
                            };
                        });

                        // En savedProtocols solo se guarda la URL de Storage o la marca liviana (sin base64)
                        setSavedProtocols(prev => {
                            const entry = findSavedProtocolEntry(prev, protocolId, taskId, unitId);
                            const cur = entry ? entry.state : currentState;
                            const curChecklist = cur.checklist || {};
                            const curItemChecklist = curChecklist[itemId] || { itemId, status: 'pending', comment: '', photos: [] };
                            const updatedPhotos = [...(curItemChecklist.photos || []), photoUrl];
                            const nextState: ProtocolState = {
                                ...cur,
                                id: protocolId,
                                checklist: {
                                    ...curChecklist,
                                    [itemId]: {
                                        ...curItemChecklist,
                                        photos: updatedPhotos
                                    }
                                },
                                updatedAt: new Date().toISOString()
                            };
                            return syncMobileProtocolKeys(prev, protocolId, nextState);
                        });

                        if (uploadFailed) {
                            showMobileAlert(
                                'Foto Guardada en Teléfono',
                                'La foto quedó guardada en tu teléfono y se subirá automáticamente cuando haya conexión.'
                            );
                        }
                    }
                }
            } catch (cameraErr: any) {
                const errMsg = (cameraErr?.message || String(cameraErr || '')).toLowerCase();
                if (errMsg.includes('denied') || errMsg.includes('permission')) {
                    setCameraPermissionDialog(true);
                } else if (errMsg.includes('no camera') || errMsg.includes('not available') || errMsg.includes('unavailable') || errMsg.includes('no_camera')) {
                    setNoCameraDialog(true);
                } else if (!errMsg.includes('cancel')) {
                    console.log("[handleAddPhoto] Camera capture error:", cameraErr);
                }
            } finally {
                setIsUploadingPhoto(false);
                setTimeout(() => {
                    (window as any).__klyd_camera_in_progress = false;
                }, 2000);
            }
            return;
        }

        // 2. Entorno Web estándar: manejar archivo desde input file
        if (e && e.target.files && e.target.files.length > 0) {
            const file = e.target.files[0];
            try {
                setIsUploadingPhoto(true);
                let photoUrl = '';
                try {
                    photoUrl = await uploadPhotoToStorage(file, protocolId, itemId);
                } catch {
                    photoUrl = await compressAndConvertPhoto(file);
                }

                if (photoUrl) {
                    setTempChecklist(prev => {
                        const curItem = prev[itemId] || { status: 'pending', comment: '', photos: [] };
                        const updatedPhotos = [...(curItem.photos || []), photoUrl];
                        return {
                            ...prev,
                            [itemId]: {
                                ...curItem,
                                photos: updatedPhotos
                            }
                        };
                    });

                    // Auto-persistencia inmediata en savedProtocols
                    setSavedProtocols(prev => {
                        const entry = findSavedProtocolEntry(prev, protocolId, taskId, unitId);
                        const cur = entry ? entry.state : currentState;
                        const curChecklist = cur.checklist || {};
                        const curItemChecklist = curChecklist[itemId] || { itemId, status: 'pending', comment: '', photos: [] };
                        const updatedPhotos = [...(curItemChecklist.photos || []), photoUrl];
                        const nextState: ProtocolState = {
                            ...cur,
                            id: protocolId,
                            checklist: {
                                ...curChecklist,
                                [itemId]: {
                                    ...curItemChecklist,
                                    photos: updatedPhotos
                                }
                            },
                            updatedAt: new Date().toISOString()
                        };
                        return syncMobileProtocolKeys(prev, protocolId, nextState);
                    });
                }
            } catch (err) {
                console.error("[handleAddPhoto] Error handling file:", err);
            } finally {
                setIsUploadingPhoto(false);
                e.target.value = '';
            }
        }
    };

    const handleDeletePhoto = (itemId: string, photoIdx: number) => {
        if (!isEditable) return;
        const photoToRemove = tempChecklist[itemId]?.photos?.[photoIdx];
        if (photoToRemove && isPendingPhotoMarker(photoToRemove)) {
            removePendingPhoto(photoToRemove).catch(err => console.warn('[handleDeletePhoto] Error eliminando foto de IndexedDB:', err));
        }

        setTempChecklist(prev => {
            const curItem = prev[itemId];
            if (!curItem || !curItem.photos) return prev;
            const updatedPhotos = curItem.photos.filter((_, i) => i !== photoIdx);
            return {
                ...prev,
                [itemId]: {
                    ...curItem,
                    photos: updatedPhotos
                }
            };
        });

        // Auto-persistencia inmediata en savedProtocols
        setSavedProtocols(prev => {
            const entry = findSavedProtocolEntry(prev, protocolId, taskId, unitId);
            const cur = entry ? entry.state : currentState;
            const curChecklist = cur.checklist || {};
            const curItemChecklist = curChecklist[itemId];
            if (!curItemChecklist || !curItemChecklist.photos) return prev;
            const updatedPhotos = curItemChecklist.photos.filter((_, i) => i !== photoIdx);
            const nextState: ProtocolState = {
                ...cur,
                id: protocolId,
                checklist: {
                    ...curChecklist,
                    [itemId]: {
                        ...curItemChecklist,
                        photos: updatedPhotos
                    }
                },
                updatedAt: new Date().toISOString()
            };
            return syncMobileProtocolKeys(prev, protocolId, nextState);
        });
    };

    const handleSaveProtocol = () => {
        const checklistToSave: Record<string, any> = {};
        Object.keys(tempChecklist).forEach(key => {
            checklistToSave[key] = { itemId: key, ...tempChecklist[key] };
        });

        const updatedSignatures = { ...currentState.signatures };
        if (currentSignature) {
            updatedSignatures[currentState.currentReviewerIndex] = currentSignature;
        }

        const isFirstSave = !resolvedEntry;
        const newState: ProtocolState = {
            ...currentState,
            id: protocolId,
            groupId: group?.id || currentState.groupId || '',
            checklist: checklistToSave,
            signatures: updatedSignatures,
            status: currentState.status === 'Inactivo' ? 'Abierto' : currentState.status,
            taskName,
            protocolName: config?.protocolName || currentState.protocolName,
            updatedAt: new Date().toISOString(),
            history: isFirstSave ? [
                ...currentState.history,
                {
                    date: new Date().toLocaleString(),
                    action: 'created',
                    user: currentUser?.name || 'Usuario',
                    snapshot: JSON.parse(JSON.stringify(checklistToSave))
                }
            ] : currentState.history
        };

        setSavedProtocols(prev => syncMobileProtocolKeys(prev, protocolId, newState));

        if (isFirstSave && addActivity) {
            addActivity({
                id: Date.now().toString(),
                timestamp: new Date().toISOString(),
                type: 'quality',
                title: 'Nuevo Protocolo Habilitado',
                description: `Se ha guardado el protocolo ${protocolId} para la partida ${taskName}`,
                user: currentUser?.name || 'Usuario'
            });
        }

        onClose();
    };

    const handleSendProtocol = async () => {
        if (!isEditable) return;

        const hasFail = items.some(item => tempChecklist[item.id]?.status === 'fail');
        if (hasFail) {
            showMobileAlert("Protocolo con Falla", "El protocolo contiene parámetros en NO CUMPLE. Por favor utilice la opción 'Rechazar' para registrar el hallazgo.", true);
            return;
        }

        const unansweredCount = items.filter(item => {
            const st = tempChecklist[item.id]?.status;
            return !st || (st !== 'pass' && st !== 'fail' && st !== 'na');
        }).length;

        const hasPhoto = hasReviewerAddedPhoto(tempChecklist, currentState);

        if (unansweredCount > 0 || !currentSignature || !hasPhoto) {
            setShowValidationHighlights(true);
            const msgs: string[] = [];
            if (!hasPhoto) msgs.push("- Adjuntar al menos 1 fotografía de evidencia correspondiente a tu revisión.");
            if (unansweredCount > 0) msgs.push(`- Responder ${unansweredCount} parámetro(s) pendiente(s).`);
            if (!currentSignature) msgs.push("- Firmar el protocolo.");
            showMobileAlert("Requisitos pendientes", msgs.join('\n'), false);
            return;
        }

        // Subida de firma a Supabase Storage con uploadSignatureIfOnline (idéntico a la web)
        let signatureToStore = currentSignature;
        if (currentSignature && currentSignature.startsWith('data:image') && typeof navigator !== 'undefined' && navigator.onLine) {
            try {
                const uploadedPath = await uploadSignatureIfOnline(
                    supabase,
                    currentSignature,
                    'quality_signatures',
                    `sig_${protocolId}_rev${currentState.currentReviewerIndex}`
                );
                if (uploadedPath) {
                    signatureToStore = uploadedPath;
                }
            } catch (sigErr) {
                console.warn('[MobileProtocolModal] Advertencia subiendo firma, conservando base64:', sigErr);
            }
        }

        const checklistToSave: Record<string, any> = {};
        Object.keys(tempChecklist).forEach(key => {
            checklistToSave[key] = { itemId: key, ...tempChecklist[key] };
        });

        const totalReviewers = group?.reviewers.length || 1;
        const isLast = currentState.currentReviewerIndex >= totalReviewers - 1;

        const newState = approveProtocol({
            currentState,
            config,
            currentReviewerName,
            currentUser,
            signatureUrl: signatureToStore || '',
            checklist: checklistToSave,
            resolutionText,
            resolutionPhotos,
            taskName,
            unitName
        });

        setSavedProtocols(prev => syncMobileProtocolKeys(prev, protocolId, newState));

        if (addActivity) {
            addActivity({
                id: Date.now().toString(),
                timestamp: new Date().toISOString(),
                type: 'quality',
                title: isLast ? 'Protocolo Aprobado y Cerrado' : 'Protocolo Aprobado',
                description: `Protocolo ${protocolId} (${taskName} - ${unitName}) ${isLast ? 'completado y cerrado' : 'avanzó al siguiente revisor'}.`,
                user: currentUser?.name || 'Usuario'
            });
        }

        onClose();
    };

    const handleConfirmReject = () => {
        if (!rejectionReason.trim()) {
            showMobileAlert("Motivo requerido", "Por favor ingrese el motivo del rechazo.", true);
            return;
        }

        const checklistToSave: Record<string, any> = {};
        Object.keys(tempChecklist).forEach(key => {
            checklistToSave[key] = { itemId: key, ...tempChecklist[key] };
        });

        const newState = rejectProtocol({
            currentState,
            currentReviewerName,
            currentUser,
            rejectionReason,
            rejectionPhotos: resolutionPhotos,
            checklist: checklistToSave,
            taskName,
            protocolName: config?.protocolName || currentState.protocolName
        });

        setSavedProtocols(prev => syncMobileProtocolKeys(prev, protocolId, newState));
        setRejectionModalOpen(false);
        setRejectionReason("");
        onClose();
    };

    const handleAdminLiberate = async () => {
        if (!currentSignature) {
            showMobileAlert("Firma requerida", "Debe firmar el protocolo antes de liberarlo como Administrador.", false);
            return;
        }

        // Subir firma admin a Supabase Storage con uploadSignatureIfOnline
        let signatureToStore = currentSignature;
        if (currentSignature && currentSignature.startsWith('data:image') && typeof navigator !== 'undefined' && navigator.onLine) {
            try {
                const uploadedPath = await uploadSignatureIfOnline(
                    supabase,
                    currentSignature,
                    'quality_signatures',
                    `sig_${protocolId}_admin_${currentState.currentReviewerIndex}`
                );
                if (uploadedPath) {
                    signatureToStore = uploadedPath;
                }
            } catch (sigErr) {
                console.warn('[MobileProtocolModal] Advertencia subiendo firma admin, conservando base64:', sigErr);
            }
        }

        const checklistToSave: Record<string, any> = {};
        Object.keys(tempChecklist).forEach(key => {
            checklistToSave[key] = { itemId: key, ...tempChecklist[key] };
        });

        const newState = adminLiberateProtocol({
            currentState,
            currentUser,
            signatureUrl: signatureToStore || '',
            checklist: checklistToSave,
            taskName,
            protocolName: config?.protocolName || currentState.protocolName
        });

        setSavedProtocols(prev => syncMobileProtocolKeys(prev, protocolId, newState));
        onClose();
    };

    // Canvas de firma táctil
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const [isDrawing, setIsDrawing] = useState(false);

    const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
        setIsDrawing(true);
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        const rect = canvas.getBoundingClientRect();
        const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
        const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
        ctx.beginPath();
        ctx.moveTo(clientX - rect.left, clientY - rect.top);
    };

    const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
        if (!isDrawing) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        const rect = canvas.getBoundingClientRect();
        const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
        const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
        ctx.lineTo(clientX - rect.left, clientY - rect.top);
        ctx.strokeStyle = '#0F172A';
        ctx.lineWidth = 2.5;
        ctx.lineCap = 'round';
        ctx.stroke();
    };

    const stopDrawing = () => {
        setIsDrawing(false);
        const ctx = canvasRef.current?.getContext('2d');
        if (ctx) ctx.closePath();
    };

    // Cálculo de progreso para barra pegajosa
    const answeredCount = items.filter(item => {
        const s = tempChecklist[item.id]?.status;
        return s === 'pass' || s === 'fail' || s === 'na';
    }).length;
    const totalItemsCount = items.length;
    const progressPercent = totalItemsCount > 0 ? (answeredCount / totalItemsCount) * 100 : 0;

    const statusColorMap: Record<string, { bg: string; text: string; border: string }> = {
        'Cerrado': { bg: '#DCFCE7', text: '#16A34A', border: '#BBF7D0' },
        'Aprobado': { bg: '#DCFCE7', text: '#16A34A', border: '#BBF7D0' },
        'Rechazado': { bg: '#FEE2E2', text: '#DC2626', border: '#FECACA' },
        'En Revisión': { bg: '#EFF6FF', text: '#2563EB', border: '#BFDBFE' },
        'Abierto': { bg: '#FEF3C7', text: '#D97706', border: '#FDE68A' }
    };
    const currentStatusStyle = statusColorMap[currentState.status] || { bg: '#F1F5F9', text: '#475569', border: '#CBD5E1' };

    return (
        <>
            <KlFullModal
                open={open}
                onClose={onClose}
                title={cleanTaskName(taskName)}
                subtitle={`Protocolo · ${unitName}${propTaskCode ? ` · #${propTaskCode}` : ''}`}
                headerActions={
                    <>
                        <button
                            type="button"
                            onClick={(e) => {
                                setHeaderMenuAnchor(e.currentTarget);
                                setIsHeaderMenuOpen(true);
                            }}
                            aria-label="Más opciones"
                            className="w-10 h-10 rounded-full hover:bg-white/15 active:bg-white/25 flex items-center justify-center transition-colors text-white"
                            title="Opciones"
                        >
                            <span className="material-symbols-outlined text-[20px]">more_vert</span>
                        </button>
                        <KlMenu
                            open={isHeaderMenuOpen}
                            onClose={() => setIsHeaderMenuOpen(false)}
                            anchorRef={{ current: headerMenuAnchor }}
                            items={[
                                {
                                    id: 'history',
                                    label: 'Historial de revisiones',
                                    iconName: 'history',
                                    onClick: () => {
                                        setIsHeaderMenuOpen(false);
                                        setHistoryModalOpen(true);
                                    }
                                },
                                ...(canReject && !isRejected && isEditable ? [
                                    {
                                        id: 'reject',
                                        label: 'Rechazar protocolo',
                                        iconName: 'cancel',
                                        iconColor: '#DC2626',
                                        onClick: () => {
                                            setIsHeaderMenuOpen(false);
                                            setRejectionModalOpen(true);
                                        }
                                    }
                                ] : []),
                                ...(isProjectAdmin() && currentState.status !== 'Cerrado' ? [
                                    {
                                        id: 'liberate',
                                        label: 'Liberar protocolo (Admin)',
                                        iconName: 'gavel',
                                        iconColor: '#D97706',
                                        onClick: () => {
                                            setIsHeaderMenuOpen(false);
                                            handleAdminLiberate();
                                        }
                                    }
                                ] : [])
                            ]}
                        />
                    </>
                }
                footer={
                    <div style={{ display: 'flex', gap: '10px', width: '100%', alignItems: 'center' }}>
                        <button
                            type="button"
                            onClick={handleSaveProtocol}
                            className="b b-o"
                            style={{
                                width: '36%',
                                flex: 'none',
                                height: '42px',
                                borderRadius: '10px',
                                background: '#F1F5F9',
                                color: '#334155',
                                borderColor: '#CBD5E1',
                                fontWeight: 600,
                                fontSize: '13px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '6px'
                            }}
                        >
                            <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>save</span>
                            <span>Guardar</span>
                        </button>
                        <button
                            type="button"
                            onClick={handleSendProtocol}
                            disabled={!isEditable}
                            className="b b-p"
                            style={{
                                width: '64%',
                                flex: 1,
                                height: '42px',
                                borderRadius: '10px',
                                background: isEditable ? '#2563EB' : '#94A3B8',
                                color: '#ffffff',
                                fontWeight: 700,
                                fontSize: '13px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '6px',
                                opacity: isEditable ? 1 : 0.6,
                                cursor: isEditable ? 'pointer' : 'not-allowed'
                            }}
                        >
                            <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>verified</span>
                            <span>
                                {currentState.currentReviewerIndex >= (group?.reviewers.length || 1) - 1
                                    ? 'Aprobar y cerrar protocolo'
                                    : 'Aprobar y pasar al siguiente'}
                            </span>
                        </button>
                    </div>
                }
            >
                <div className="flex flex-col gap-3 pb-6">
                    {/* Franja de estado: Estado del protocolo en su color y Revisor N · Paso N de N */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        background: '#ffffff',
                        padding: '9px 12px',
                        borderRadius: '10px',
                        border: '1px solid #E2E8F0',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
                    }}>
                        <span style={{
                            fontSize: '11px',
                            fontWeight: 700,
                            padding: '2px 8px',
                            borderRadius: '6px',
                            background: currentStatusStyle.bg,
                            color: currentStatusStyle.text,
                            border: `1px solid ${currentStatusStyle.border}`
                        }}>
                            {currentState.status}
                        </span>
                        {isProjectAdmin() && group && group.reviewers.length > 1 ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <label style={{ fontSize: '10px', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                    Paso:
                                </label>
                                <select
                                    value={currentState.currentReviewerIndex}
                                    onChange={(e) => {
                                        const newIdx = parseInt(e.target.value);
                                        setSavedProtocols(prev => {
                                            const entry = findSavedProtocolEntry(prev, protocolId, taskId, unitId);
                                            const current = entry ? entry.state : currentState;
                                            return syncMobileProtocolKeys(prev, protocolId, {
                                                ...current,
                                                currentReviewerIndex: newIdx,
                                                updatedAt: new Date().toISOString()
                                            });
                                        });
                                        const savedState = resolvedEntry?.state || savedProtocols[protocolId];
                                        setCurrentSignature(savedState?.signatures[newIdx] || null);
                                    }}
                                    style={{
                                        fontSize: '12px',
                                        fontWeight: 700,
                                        color: '#2563EB',
                                        background: '#F8FAFC',
                                        border: '1px solid #CBD5E1',
                                        borderRadius: '6px',
                                        padding: '3px 8px',
                                        outline: 'none'
                                    }}
                                >
                                    {group.reviewers.map((rev, i) => (
                                        <option key={i} value={i}>
                                            Revisor {i + 1} ({rev.name})
                                        </option>
                                    ))}
                                </select>
                            </div>
                        ) : (
                            <span style={{ fontSize: '12px', fontWeight: 600, color: '#475569' }}>
                                Revisor {(currentState.currentReviewerIndex || 0) + 1} · Paso {(currentState.currentReviewerIndex || 0) + 1} de {group?.reviewers.length || 1}
                            </span>
                        )}
                    </div>

                    {!isEditable && (
                        <div className="bg-amber-50 dark:bg-amber-950/20 text-amber-800 dark:text-amber-300 text-[11px] p-2.5 rounded-lg flex items-center gap-1.5 font-medium">
                            <span className="material-symbols-outlined text-[15px]">info</span>
                            Modo solo lectura (protocolo cerrado o no corresponde a su turno de revisión).
                        </div>
                    )}

                    {/* Banner Alerta Fotos Obligatorias */}
                    {showValidationHighlights && !items.some(item => tempChecklist[item.id]?.photos && tempChecklist[item.id].photos.some(p => typeof p === 'string' && p.trim() !== '')) && (
                        <div className="p-3 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900/40 rounded-xl flex items-center gap-2 text-red-600 dark:text-red-400 text-xs font-bold">
                            <span className="material-symbols-outlined text-[18px]">photo_camera</span>
                            <span>⚠️ Debe adjuntar al menos una fotografía de evidencia.</span>
                        </div>
                    )}

                    {/* Rejection Details if Rejected */}
                    {isRejected && (
                        <div className="bg-rose-50 dark:bg-rose-950/20 border border-rose-200 dark:border-rose-900/40 rounded-xl p-3 text-xs flex flex-col gap-2">
                            <div className="font-bold text-rose-700 flex items-center gap-1">
                                <span className="material-symbols-outlined text-[16px]">warning</span>
                                Protocolo con Observaciones / Rechazado
                            </div>
                            <p className="text-slate-700 dark:text-slate-300 italic">
                                "{currentState.history.filter(h => h.action === 'rejected').pop()?.comment || 'Requiere corrección'}"
                            </p>
                        </div>
                    )}

                    {/* Progreso pegajoso (sticky): "N de M evaluados" y barra azul de 4px */}
                    <div style={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 15,
                        background: '#F1F5F9',
                        paddingTop: '6px',
                        paddingBottom: '8px',
                        margin: '-2px 0 2px'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '5px' }}>
                            <span style={{ fontSize: '12px', fontWeight: 700, color: '#334155' }}>
                                {answeredCount} de {totalItemsCount} evaluados
                            </span>
                            <span style={{ fontSize: '11px', fontWeight: 700, color: '#2563EB' }}>
                                {Math.round(progressPercent)}%
                            </span>
                        </div>
                        <div style={{ height: '4px', width: '100%', background: '#CBD5E1', borderRadius: '2px', overflow: 'hidden' }}>
                            <div style={{
                                height: '100%',
                                width: `${progressPercent}%`,
                                background: '#2563EB',
                                transition: 'width 0.2s ease',
                                borderRadius: '2px'
                            }} />
                        </div>
                    </div>

                    {/* Lista de Parámetros del Protocolo */}
                    <div className="flex flex-col gap-2.5">
                        {items.length === 0 ? (
                            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6 text-center text-slate-400 text-xs italic">
                                No hay parámetros de revisión configurados para este protocolo.
                            </div>
                        ) : (
                            items.map((item, idx) => {
                                const itemState = tempChecklist[item.id] || { status: 'pending', comment: '', photos: [] };
                                const isAnswered = itemState.status === 'pass' || itemState.status === 'fail' || itemState.status === 'na';
                                const highlightItem = showValidationHighlights && !isAnswered;
                                const isFail = itemState.status === 'fail';

                                return (
                                    <div
                                        key={item.id}
                                        style={{
                                            background: isFail ? '#FEF2F2' : '#ffffff',
                                            border: isFail ? '1.5px solid #FCA5A5' : highlightItem ? '1.5px solid #F87171' : '1px solid #E2E8F0',
                                            borderRadius: '12px',
                                            padding: '12px',
                                            boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '8px'
                                        }}
                                    >
                                        <div className="flex items-start gap-2">
                                            <span style={{ fontWeight: 700, color: '#0F172A', fontSize: '12px', marginTop: '1px' }}>
                                                {idx + 1}.
                                            </span>
                                            <span style={{ color: '#1E293B', fontSize: '12px', lineHeight: '1.4', flex: 1 }}>
                                                {item.text}
                                            </span>
                                            {item.additionalInfo && (
                                                <button
                                                    type="button"
                                                    onClick={() => setActiveInfoItem({ id: item.id, text: item.additionalInfo || '' })}
                                                    className="text-slate-400 hover:text-slate-600 p-1"
                                                    title="Información"
                                                >
                                                    <span className="material-symbols-outlined text-[16px]">info</span>
                                                </button>
                                            )}
                                        </div>

                                        {/* Botones de Evaluación Móvil (34px de alto) */}
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginTop: '4px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                {/* CUMPLE */}
                                                <button
                                                    type="button"
                                                    onClick={() => handleChecklistClick(item.id, 'pass')}
                                                    disabled={!isEditable}
                                                    style={{
                                                        height: '34px',
                                                        padding: '0 10px',
                                                        borderRadius: '8px',
                                                        fontSize: '12px',
                                                        fontWeight: 700,
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '4px',
                                                        background: itemState.status === 'pass' ? '#16A34A' : '#F8FAFC',
                                                        color: itemState.status === 'pass' ? '#ffffff' : '#334155',
                                                        border: itemState.status === 'pass' ? '1px solid #16A34A' : '1px solid #CBD5E1',
                                                        cursor: isEditable ? 'pointer' : 'default',
                                                        boxShadow: itemState.status === 'pass' ? '0 1px 2px rgba(22,163,74,0.3)' : 'none'
                                                    }}
                                                >
                                                    <span className="material-symbols-outlined" style={{ fontSize: '15px' }}>check_circle</span>
                                                    <span>Cumple</span>
                                                </button>

                                                {/* FALLA */}
                                                <button
                                                    type="button"
                                                    onClick={() => handleChecklistClick(item.id, 'fail')}
                                                    disabled={!isEditable}
                                                    style={{
                                                        height: '34px',
                                                        padding: '0 10px',
                                                        borderRadius: '8px',
                                                        fontSize: '12px',
                                                        fontWeight: 700,
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '4px',
                                                        background: itemState.status === 'fail' ? '#DC2626' : '#F8FAFC',
                                                        color: itemState.status === 'fail' ? '#ffffff' : '#334155',
                                                        border: itemState.status === 'fail' ? '1px solid #DC2626' : '1px solid #CBD5E1',
                                                        cursor: isEditable ? 'pointer' : 'default',
                                                        boxShadow: itemState.status === 'fail' ? '0 1px 2px rgba(220,38,38,0.3)' : 'none'
                                                    }}
                                                >
                                                    <span className="material-symbols-outlined" style={{ fontSize: '15px' }}>cancel</span>
                                                    <span>Falla</span>
                                                </button>

                                                {/* N/A */}
                                                <button
                                                    type="button"
                                                    onClick={() => handleChecklistClick(item.id, 'na')}
                                                    disabled={!isEditable}
                                                    style={{
                                                        height: '34px',
                                                        padding: '0 10px',
                                                        borderRadius: '8px',
                                                        fontSize: '12px',
                                                        fontWeight: 700,
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '4px',
                                                        background: itemState.status === 'na' ? '#334155' : '#F8FAFC',
                                                        color: itemState.status === 'na' ? '#ffffff' : '#334155',
                                                        border: itemState.status === 'na' ? '1px solid #334155' : '1px solid #CBD5E1',
                                                        cursor: isEditable ? 'pointer' : 'default',
                                                        boxShadow: itemState.status === 'na' ? '0 1px 2px rgba(51,65,85,0.3)' : 'none'
                                                    }}
                                                >
                                                    <span>N/A</span>
                                                </button>
                                            </div>

                                            {/* Comentarios y Fotos */}
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <button
                                                    type="button"
                                                    onClick={() => setActiveCommentItem({ id: item.id, text: itemState.comment || '' })}
                                                    style={{
                                                        height: '34px',
                                                        padding: '0 9px',
                                                        borderRadius: '8px',
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        gap: '4px',
                                                        cursor: 'pointer',
                                                        border: (isFail && !itemState.comment?.trim())
                                                            ? '1.5px solid #F87171'
                                                            : itemState.comment?.trim()
                                                                ? '1px solid #FCD34D'
                                                                : '1px solid #CBD5E1',
                                                        background: (isFail && !itemState.comment?.trim())
                                                            ? '#FEE2E2'
                                                            : itemState.comment?.trim()
                                                                ? '#FEF3C7'
                                                                : '#F8FAFC',
                                                        color: (isFail && !itemState.comment?.trim())
                                                            ? '#DC2626'
                                                            : itemState.comment?.trim()
                                                                ? '#D97706'
                                                                : '#64748B'
                                                    }}
                                                    title="Comentario"
                                                >
                                                    <span className="material-symbols-outlined" style={{ fontSize: '17px' }}>chat</span>
                                                    {itemState.comment?.trim() && (
                                                        <span style={{ fontSize: '11px', fontWeight: 700 }}>1</span>
                                                    )}
                                                </button>

                                                {Capacitor.isNativePlatform() ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleAddPhoto(item.id)}
                                                        disabled={!isEditable || isUploadingPhoto}
                                                        style={{
                                                            height: '34px',
                                                            padding: '0 9px',
                                                            borderRadius: '8px',
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            gap: '4px',
                                                            cursor: isEditable && !isUploadingPhoto ? 'pointer' : 'default',
                                                            border: (itemState.photos && itemState.photos.length > 0)
                                                                ? '1px solid #93C5FD'
                                                                : '1px solid #CBD5E1',
                                                            background: (itemState.photos && itemState.photos.length > 0)
                                                                ? '#EFF6FF'
                                                                : '#F8FAFC',
                                                            color: (itemState.photos && itemState.photos.length > 0)
                                                                ? '#2563EB'
                                                                : '#64748B'
                                                        }}
                                                        title="Adjuntar Foto"
                                                    >
                                                        <span className="material-symbols-outlined" style={{ fontSize: '17px' }}>
                                                            {isUploadingPhoto ? 'sync' : 'photo_camera'}
                                                        </span>
                                                        {itemState.photos && itemState.photos.length > 0 && (
                                                            <span style={{ fontSize: '11px', fontWeight: 700 }}>
                                                                {itemState.photos.length}
                                                            </span>
                                                        )}
                                                    </button>
                                                ) : (
                                                    <label
                                                        style={{
                                                            height: '34px',
                                                            padding: '0 9px',
                                                            borderRadius: '8px',
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            gap: '4px',
                                                            cursor: isEditable && !isUploadingPhoto ? 'pointer' : 'default',
                                                            border: (itemState.photos && itemState.photos.length > 0)
                                                                ? '1px solid #93C5FD'
                                                                : '1px solid #CBD5E1',
                                                            background: (itemState.photos && itemState.photos.length > 0)
                                                                ? '#EFF6FF'
                                                                : '#F8FAFC',
                                                            color: (itemState.photos && itemState.photos.length > 0)
                                                                ? '#2563EB'
                                                                : '#64748B'
                                                        }}
                                                        title="Adjuntar Foto"
                                                    >
                                                        <span className="material-symbols-outlined" style={{ fontSize: '17px' }}>
                                                            {isUploadingPhoto ? 'sync' : 'photo_camera'}
                                                        </span>
                                                        {itemState.photos && itemState.photos.length > 0 && (
                                                            <span style={{ fontSize: '11px', fontWeight: 700 }}>
                                                                {itemState.photos.length}
                                                            </span>
                                                        )}
                                                        <input
                                                            type="file"
                                                            accept="image/*"
                                                            capture="environment"
                                                            className="hidden"
                                                            disabled={!isEditable || isUploadingPhoto}
                                                            onChange={(e) => handleAddPhoto(item.id, e)}
                                                        />
                                                    </label>
                                                )}
                                            </div>
                                        </div>

                                        {/* Mensaje de advertencia si se marca Falla y no tiene comentario */}
                                        {isFail && !itemState.comment?.trim() && (
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                                color: '#DC2626',
                                                fontSize: '11px',
                                                fontWeight: 600,
                                                marginTop: '2px',
                                                padding: '2px 4px'
                                            }}>
                                                <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>warning</span>
                                                <span>Agrega un comentario para registrar la falla.</span>
                                            </div>
                                        )}
                                        {/* Fotos miniaturas adjuntas */}
                                        {itemState.photos && itemState.photos.length > 0 && (
                                            <div className="flex flex-wrap gap-1.5 mt-1">
                                                {itemState.photos.map((ph, pIdx) => {
                                                    const isPending = isPendingPhotoMarker(ph);
                                                    if (isPending) {
                                                        return (
                                                            <div
                                                                key={pIdx}
                                                                className="relative h-11 px-2.5 rounded-lg flex items-center gap-1.5 bg-amber-50 dark:bg-amber-950/30 border border-amber-300 dark:border-amber-700/60 text-amber-800 dark:text-amber-200 select-none shadow-sm"
                                                                title="Foto pendiente de subir desde el dispositivo"
                                                            >
                                                                <span className="material-symbols-outlined text-[16px] text-amber-600 dark:text-amber-400 animate-pulse">cloud_upload</span>
                                                                <span className="text-[10px] font-bold tracking-tight">Foto pendiente de subir desde el dispositivo</span>
                                                                {isEditable && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleDeletePhoto(item.id, pIdx)}
                                                                        className="ml-1 text-slate-400 hover:text-red-500 font-bold text-xs"
                                                                        title="Eliminar foto pendiente"
                                                                    >
                                                                        ×
                                                                    </button>
                                                                )}
                                                            </div>
                                                        );
                                                    }
                                                    return (
                                                        <div key={pIdx} className="relative size-12 rounded overflow-hidden border border-slate-200">
                                                            <img
                                                                src={getPhotoUrl(ph)}
                                                                alt="Foto"
                                                                className="w-full h-full object-cover cursor-pointer"
                                                                onClick={() => setPreviewImageUrl(ph)}
                                                            />
                                                            {isEditable && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleDeletePhoto(item.id, pIdx)}
                                                                    className="absolute top-0 right-0 bg-red-600 text-white size-3.5 rounded-bl flex items-center justify-center text-[10px]"
                                                                >
                                                                    ×
                                                                </button>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        {/* Comentario texto visible */}
                                        {itemState.comment && (
                                            <div className="bg-amber-50/60 dark:bg-amber-950/20 text-slate-700 dark:text-slate-300 text-[11px] p-2 rounded border border-amber-200/60 italic">
                                                "{itemState.comment}"
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        )}
                    </div>

                    {/* Firma Digital del Revisor */}
                    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm p-3 flex flex-col gap-2">
                        <div className="text-xs font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wide">
                            Firma del Revisor: {currentReviewerName}
                        </div>

                        {currentSignature ? (
                            <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-2 bg-white flex flex-col items-center justify-center relative min-h-[90px]">
                                <img
                                    src={getPhotoUrl(currentSignature)}
                                    alt="Firma"
                                    className="max-h-20 object-contain"
                                />
                                {isEditable && (
                                    <button
                                        type="button"
                                        onClick={() => setCurrentSignature(null)}
                                        className="text-[10px] text-red-600 font-bold hover:underline mt-1"
                                    >
                                        Cambiar Firma
                                    </button>
                                )}
                            </div>
                        ) : (
                            <button
                                type="button"
                                onClick={() => isEditable && setSignaturePadOpen(true)}
                                disabled={!isEditable}
                                className={`border-2 border-dashed rounded-lg p-4 text-center transition-all flex flex-col items-center justify-center gap-1 ${isEditable
                                    ? 'border-blue-300 hover:border-blue-500 bg-blue-50/30 text-blue-700 cursor-pointer active:scale-98'
                                    : 'border-slate-300 bg-slate-50 text-slate-400 cursor-not-allowed'
                                    }`}
                            >
                                <span className="material-symbols-outlined text-[24px]">draw</span>
                                <span className="text-xs font-bold">Toque aquí para dibujar su firma</span>
                            </button>
                        )}
                    </div>
                </div>
            </KlFullModal>

            {/* Modal Secundario: Pad de Firma Táctil */}
            {signaturePadOpen && (
                <div className="fixed inset-0 z-[120] bg-black/70 backdrop-blur-sm flex items-center justify-center p-3">
                    <div className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl flex flex-col">
                        <div className="p-3 bg-blue-600 text-white flex justify-between items-center">
                            <h3 className="font-bold text-sm">Firma Digital · {currentReviewerName}</h3>
                            <button type="button" onClick={() => setSignaturePadOpen(false)} className="text-white/80 hover:text-white">
                                <span className="material-symbols-outlined text-[20px]">close</span>
                            </button>
                        </div>
                        <div className="p-3 flex flex-col items-center gap-2 bg-slate-50 dark:bg-slate-800/50">
                            <p className="text-[11px] text-slate-500 text-center">Dibuje su firma con el dedo en el recuadro:</p>
                            <div className="bg-white rounded-xl border border-slate-300 overflow-hidden touch-none w-full aspect-[2/1]">
                                <canvas
                                    ref={canvasRef}
                                    width={340}
                                    height={170}
                                    className="w-full h-full cursor-crosshair bg-white"
                                    onMouseDown={startDrawing}
                                    onMouseMove={draw}
                                    onMouseUp={stopDrawing}
                                    onMouseLeave={stopDrawing}
                                    onTouchStart={startDrawing}
                                    onTouchMove={draw}
                                    onTouchEnd={stopDrawing}
                                />
                            </div>
                            <button
                                type="button"
                                onClick={() => {
                                    const canvas = canvasRef.current;
                                    if (canvas) {
                                        const ctx = canvas.getContext('2d');
                                        if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
                                    }
                                }}
                                className="text-[11px] font-bold text-slate-500 hover:text-red-500 mt-1"
                            >
                                Limpiar trazo
                            </button>
                        </div>
                        <div className="p-3 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => setSignaturePadOpen(false)}
                                className="px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-lg"
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    if (canvasRef.current) {
                                        const optimized = optimizeSignatureCanvas(canvasRef.current);
                                        if (!optimized) {
                                            showMobileAlert("Firma requerida", "Por favor trace su firma en el recuadro antes de confirmar.", false);
                                            return;
                                        }
                                        setCurrentSignature(optimized);
                                        setSignaturePadOpen(false);
                                    }
                                }}
                                className="px-4 py-1.5 text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm"
                            >
                                Guardar Firma
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Secundario: Comentario */}
            {activeCommentItem && (
                <div className="fixed inset-0 z-[120] bg-black/70 backdrop-blur-sm flex items-center justify-center p-3">
                    <div className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl flex flex-col p-4 gap-3">
                        <div className="font-bold text-sm text-slate-800 dark:text-white flex justify-between items-center">
                            <span>Comentario del parámetro</span>
                            <button type="button" onClick={() => setActiveCommentItem(null)} className="text-slate-400">
                                <span className="material-symbols-outlined text-[18px]">close</span>
                            </button>
                        </div>
                        <BufferedTextArea
                            value={activeCommentItem.text}
                            onValueChange={(val) => setActiveCommentItem({ ...activeCommentItem, text: val })}
                            placeholder="Escriba aquí la observación..."
                            className="w-full h-24 p-2.5 text-xs border border-slate-300 dark:border-slate-700 rounded-lg resize-none focus:ring-1 focus:ring-blue-500 outline-none"
                            disabled={!isEditable}
                        />
                        <div className="flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => setActiveCommentItem(null)}
                                className="px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-lg"
                            >
                                Cancelar
                            </button>
                            {isEditable && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setTempChecklist(prev => ({
                                            ...prev,
                                            [activeCommentItem.id]: {
                                                ...(prev[activeCommentItem.id] || { status: 'pending', photos: [] }),
                                                comment: activeCommentItem.text.trim()
                                            }
                                        }));
                                        setActiveCommentItem(null);
                                    }}
                                    className="px-4 py-1.5 text-xs font-bold text-white bg-blue-600 rounded-lg"
                                >
                                    Guardar
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Secundario: Info Adicional */}
            {activeInfoItem && (
                <div className="fixed inset-0 z-[120] bg-black/70 backdrop-blur-sm flex items-center justify-center p-3">
                    <div className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl flex flex-col p-4 gap-3">
                        <div className="font-bold text-sm text-slate-800 dark:text-white flex justify-between items-center">
                            <span>Información del Parámetro</span>
                            <button type="button" onClick={() => setActiveInfoItem(null)} className="text-slate-400">
                                <span className="material-symbols-outlined text-[18px]">close</span>
                            </button>
                        </div>
                        <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed whitespace-pre-line">
                            {activeInfoItem.text}
                        </p>
                        <div className="flex justify-end">
                            <button
                                type="button"
                                onClick={() => setActiveInfoItem(null)}
                                className="px-4 py-1.5 text-xs font-bold text-white bg-blue-600 rounded-lg"
                            >
                                Entendido
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Secundario: Rechazar con Motivo */}
            {rejectionModalOpen && (
                <div className="fixed inset-0 z-[120] bg-black/70 backdrop-blur-sm flex items-center justify-center p-3">
                    <div className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl flex flex-col p-4 gap-3">
                        <div className="font-bold text-sm text-rose-600 flex items-center gap-1.5">
                            <span className="material-symbols-outlined text-[18px]">warning</span>
                            <span>Rechazar Protocolo</span>
                        </div>
                        <p className="text-xs text-slate-500">
                            El protocolo volverá a revisión anterior con estado Rechazado. Indique el motivo del hallazgo:
                        </p>
                        <BufferedTextArea
                            value={rejectionReason}
                            onValueChange={(val) => setRejectionReason(val)}
                            placeholder="Describa el motivo del rechazo..."
                            className="w-full h-24 p-2.5 text-xs border border-rose-300 rounded-lg resize-none focus:ring-1 focus:ring-rose-500 outline-none"
                        />
                        <div className="flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => setRejectionModalOpen(false)}
                                className="px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-lg"
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                onClick={handleConfirmReject}
                                className="px-4 py-1.5 text-xs font-bold text-white bg-rose-600 hover:bg-rose-700 rounded-lg shadow-sm"
                            >
                                Confirmar Rechazo
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Secundario: Historial */}
            {historyModalOpen && (
                <div className="fixed inset-0 z-[120] bg-black/70 backdrop-blur-sm flex items-center justify-center p-3">
                    <div className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-md max-h-[80vh] overflow-hidden shadow-2xl flex flex-col">
                        <div className="p-3 bg-slate-800 text-white flex justify-between items-center">
                            <h3 className="font-bold text-sm flex items-center gap-1.5">
                                <span className="material-symbols-outlined text-[18px]">history</span>
                                Historial de Eventos
                            </h3>
                            <button type="button" onClick={() => setHistoryModalOpen(false)} className="text-white/80 hover:text-white">
                                <span className="material-symbols-outlined text-[20px]">close</span>
                            </button>
                        </div>
                        <div className="p-4 flex-1 overflow-y-auto space-y-3">
                            {currentState.history.length === 0 ? (
                                <p className="text-center text-slate-400 py-6 text-xs italic">
                                    No hay registros históricos previos.
                                </p>
                            ) : (
                                currentState.history.map((h, i) => (
                                    <div key={i} className="border-l-2 border-blue-500 pl-3 py-1 text-xs">
                                        <div className="flex items-center justify-between text-[10px] text-slate-400">
                                            <span>{h.date}</span>
                                            <span className="font-bold uppercase text-slate-600 dark:text-slate-300">{h.action}</span>
                                        </div>
                                        <div className="font-bold text-slate-800 dark:text-slate-200 mt-0.5">{h.user}</div>
                                        {h.comment && <p className="text-slate-600 dark:text-slate-400 mt-1 italic">"{h.comment}"</p>}
                                    </div>
                                ))
                            )}
                        </div>
                        <div className="p-3 bg-slate-50 dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700 flex justify-end">
                            <button
                                type="button"
                                onClick={() => setHistoryModalOpen(false)}
                                className="px-4 py-1.5 text-xs font-bold text-white bg-slate-700 rounded-lg"
                            >
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Lightbox / Preview de Imagen */}
            {previewImageUrl && (
                <div
                    className="fixed inset-0 z-[130] bg-black/85 backdrop-blur-sm flex items-center justify-center p-3 animate-fade-in"
                    onClick={() => setPreviewImageUrl(null)}
                >
                    <button
                        type="button"
                        onClick={() => setPreviewImageUrl(null)}
                        className="absolute top-4 right-4 text-white/90 bg-black/50 p-2 rounded-full"
                    >
                        <span className="material-symbols-outlined text-[24px]">close</span>
                    </button>
                    {isPendingPhotoMarker(previewImageUrl) ? (
                        <div
                            className="bg-slate-900/95 border border-amber-500/40 rounded-2xl p-6 text-center text-amber-200 flex flex-col items-center max-w-xs shadow-2xl"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <span className="material-symbols-outlined text-4xl text-amber-400 animate-pulse mb-2">cloud_upload</span>
                            <h4 className="text-sm font-bold mb-1 leading-snug">Foto pendiente de subir desde el dispositivo</h4>
                            <p className="text-[11px] text-slate-300 leading-relaxed">La fotografía fue capturada en terreno y se sincronizará automáticamente al servidor cuando haya conexión a internet.</p>
                        </div>
                    ) : (
                        <img
                            src={getPhotoUrl(previewImageUrl)}
                            alt="Evidencia ampliada"
                            className="max-w-[92vw] max-h-[85vh] object-contain rounded-xl shadow-2xl"
                            onClick={(e) => e.stopPropagation()}
                        />
                    )}
                </div>
            )}

            {/* Diálogo KlDialog para Alertas y Validaciones Móviles */}
            <KlDialog
                open={alertDialog.open}
                onClose={() => setAlertDialog(prev => ({ ...prev, open: false }))}
                title={alertDialog.title}
                message={alertDialog.message}
                confirmText="Entendido"
                cancelText={null}
                danger={alertDialog.danger}
            />

            {/* Diálogo KlDialog para Permiso de Cámara Denegado */}
            <KlDialog
                open={cameraPermissionDialog}
                onClose={() => setCameraPermissionDialog(false)}
                title="Permiso de Cámara Requerido"
                message="Para adjuntar fotos, activa el permiso de cámara en Ajustes"
                confirmText="Abrir Ajustes"
                cancelText="Cancelar"
                onConfirm={handleOpenSettings}
            />

            {/* Diálogo KlDialog para Dispositivo Sin Cámara */}
            <KlDialog
                open={noCameraDialog}
                onClose={() => setNoCameraDialog(false)}
                title="Cámara no disponible"
                message="Este dispositivo no tiene cámara disponible."
                confirmText="Entendido"
                cancelText={null}
            />
        </>
    );
};

export default MobileProtocolModal;
